import { useState, useEffect, useCallback } from 'react';
import { apiFetch, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, X, Clock, MapPin, Calendar } from 'lucide-react';

const DAYS = ['friday', 'saturday', 'sunday'];
const DAY_LABELS = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const ScheduleTab = () => {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeDay, setActiveDay] = useState('friday');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, settRes] = await Promise.all([
        apiFetch(`${API_BASE}/schedule/admin/items`),
        apiFetch(`${API_BASE}/schedule/settings`),
      ]);
      if (itemsRes.ok) setItems(await itemsRes.json());
      if (settRes.ok) setSettings(await settRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async (updates) => {
    const res = await apiFetch(`${API_BASE}/schedule/admin/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
    });
    if (res.ok) setSettings(await res.json());
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule item?')) return;
    await apiFetch(`${API_BASE}/schedule/admin/items/${id}`, { method: 'DELETE' });
    fetchAll();
  };

  const dayItems = items.filter(i => i.day === activeDay).sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div data-testid="schedule-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-brand-dark">Event Schedule</h2>
          <p className="text-sm text-gray-500">Manage the public event schedule for the retreat</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-schedule-item-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Item
        </Button>
      </div>

      {/* Settings */}
      {settings && (
        <div className="bg-white border p-4 mb-6 flex flex-wrap items-center gap-6" data-testid="schedule-settings">
          <div className="flex items-center gap-3">
            <Switch checked={settings.enabled} onCheckedChange={v => saveSettings({ enabled: v })} data-testid="schedule-enabled-switch" />
            <div>
              <Label className="font-medium">Schedule Page Visible</Label>
              <p className="text-xs text-gray-500">Show/hide the schedule page on the website</p>
            </div>
          </div>
          <div>
            <Label className="text-xs">Page Title</Label>
            <Input value={settings.page_title || ''} onChange={e => setSettings(s => ({ ...s, page_title: e.target.value }))} onBlur={() => saveSettings({ page_title: settings.page_title })} className="w-48 h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Subtitle</Label>
            <Input value={settings.page_subtitle || ''} onChange={e => setSettings(s => ({ ...s, page_subtitle: e.target.value }))} onBlur={() => saveSettings({ page_subtitle: settings.page_subtitle })} className="w-64 h-8 text-sm" />
          </div>
        </div>
      )}

      {/* Day Tabs */}
      <div className="flex gap-2 mb-4">
        {DAYS.map(d => (
          <button key={d} onClick={() => setActiveDay(d)} className={`px-4 py-2 text-sm font-medium capitalize ${activeDay === d ? 'bg-brand-dark text-white' : 'bg-white text-brand-dark border hover:bg-gray-50'}`} data-testid={`admin-day-${d}`}>
            {DAY_LABELS[d]} ({items.filter(i => i.day === d).length})
          </button>
        ))}
      </div>

      {showForm && (
        <ScheduleItemForm
          item={editing}
          defaultDay={activeDay}
          nextOrder={dayItems.length + 1}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }}
        />
      )}

      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading...</p>
      ) : dayItems.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">No items for {DAY_LABELS[activeDay]}. Add the first one!</p>
      ) : (
        <div className="space-y-2">
          {dayItems.map(item => (
            <div key={item.id} className={`bg-white border p-3 flex items-center gap-3 ${!item.is_active ? 'opacity-50' : ''}`} data-testid={`schedule-row-${item.id}`}>
              <div className="w-20 text-right flex-shrink-0">
                <p className="text-sm font-bold text-brand-dark">{item.time_start}</p>
                {item.time_end && <p className="text-xs text-gray-400">{item.time_end}</p>}
              </div>
              <div className="w-px h-10 bg-brand-gold/30 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-dark truncate">{item.title}</p>
                {item.location && <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => { setEditing(item); setShowForm(true); }} className="p-1.5 hover:bg-gray-100 rounded"><Pencil className="w-4 h-4 text-blue-600" /></button>
                <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-gray-100 rounded"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ScheduleItemForm = ({ item, defaultDay, nextOrder, onClose, onSaved }) => {
  const [form, setForm] = useState({
    day: item?.day || defaultDay,
    time_start: item?.time_start || '',
    time_end: item?.time_end || '',
    title: item?.title || '',
    description: item?.description || '',
    location: item?.location || '',
    sort_order: item?.sort_order ?? nextOrder,
    is_active: item?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const url = item ? `${API_BASE}/schedule/admin/items/${item.id}` : `${API_BASE}/schedule/admin/items`;
    const method = item ? 'PUT' : 'POST';
    const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) onSaved();
    setSaving(false);
  };

  return (
    <div className="bg-white border p-5 mb-4" data-testid="schedule-item-form">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-brand-dark">{item ? 'Edit Item' : 'Add Schedule Item'}</h3>
        <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Day</Label>
            <select value={form.day} onChange={e => setForm(f => ({ ...f, day: e.target.value }))} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="schedule-day-select">
              {DAYS.map(d => <option key={d} value={d}>{DAY_LABELS[d]}</option>)}
            </select>
          </div>
          <div>
            <Label>Start Time</Label>
            <Input value={form.time_start} onChange={e => setForm(f => ({ ...f, time_start: e.target.value }))} placeholder="8:00 AM" data-testid="schedule-time-start" />
          </div>
          <div>
            <Label>End Time</Label>
            <Input value={form.time_end} onChange={e => setForm(f => ({ ...f, time_end: e.target.value }))} placeholder="9:00 AM" data-testid="schedule-time-end" />
          </div>
          <div>
            <Label>Order</Label>
            <Input type="number" min="0" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Title *</Label>
            <Input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Morning Yoga" data-testid="schedule-title-input" />
          </div>
          <div>
            <Label>Location</Label>
            <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Main Stage" data-testid="schedule-location-input" />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm" placeholder="Optional details..." data-testid="schedule-desc-input" />
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={saving} data-testid="schedule-save-btn">{saving ? 'Saving...' : item ? 'Update' : 'Add Item'}</Button>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
};

export default ScheduleTab;
